import { router } from "@inertiajs/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Layout from "@/components/Layout";
import Table from "@/components/Table";
import Typography from "@/components/Typography";
import WarningTooltip from "@/components/WarningTooltip";
import { FiltersContainer, ListSection, PageContainer } from "@/constants/listIndexStyle";
import { completMonth } from "@/utils/dateFormater";
import { getYearOptions } from "@/utils/getYearOptions";
import { formatNumberWithCommas } from "@/utils/numberFormater";

const yearsOptions: string[] = getYearOptions();

interface FacilityRow {
  id: number;
  code: string;
  address: string;
  consumer_id: number;
  consumer_name: string;
  power_plant_id: number;
  power_plant_name: string;
  consortium_id: number;
  consortium_name: string;
  allocation_type: "percentage" | "order" | "none";
  allocation_value: number;
  total_injected: number;
  injected_in_facility: number;
  monthly_kwh: number;
  compensated_kwh: number;
}

interface ConsumerRow {
  id: number;
  name: string;
  monthly_kwh: number;
  compensated_kwh: number;
  facilities: FacilityRow[];
}

interface InjectedEnergyPageProps {
  consumers: ConsumerRow[];
  year: number;
  month: number;
}

const InjectedEnergyPage = ({ consumers, year, month }: InjectedEnergyPageProps) => {
  const columnHelper = createColumnHelper<ConsumerRow>();
  const today = new Date();

  const [filters, setFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(year || today.getFullYear()),
    month: new URLSearchParams(window.location.search).get("month")
      ? completMonth[Number(new URLSearchParams(window.location.search).get("month")) - 1]
      : completMonth[month ? month - 1 : today.getMonth() - 1],
  });

  const updateFilter = (key: "year" | "month", value: string) => {
    if (filters[key] === value) return;

    const nextFilters = { ...filters, [key]: value };
    const monthIndex = completMonth.indexOf(nextFilters.month) + 1;

    setFilters(nextFilters);
    router.get(
      "/manager/injected-energy",
      {
        year: nextFilters.year,
        month: monthIndex > 0 ? monthIndex : 1,
      },
      {
        preserveState: true,
        replace: true,
      },
    );
  };

  // Transformar os dados para o formato hierárquico com subRows
  const tableData = consumers
    .filter((consumer) => consumer.facilities.length > 0)
    .map((consumer) => ({
      id: consumer.id,
      name: consumer.name,
      monthly_kwh: consumer.monthly_kwh,
      compensated_kwh: consumer.compensated_kwh,
      subRows: consumer.facilities,
    }));

  const columns = [
    columnHelper.accessor("name", {
      header: () => <div className="w-full pl-6 text-left">Consumer / Facility</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // Linha PAI: Consumer
          const facilityCode = info.row.original.subRows?.[0]?.code;

          return (
            <div className="flex w-full items-center gap-2 pl-6 text-left">
              <span className="font-semibold text-primary500">
                {info.row.original.name}
                {"  "}
                {facilityCode ? `(${facilityCode})` : ""}
              </span>
            </div>
          );
        }
        // Linha FILHA: Facility
        return (
          <div className="flex w-full flex-col pl-6 text-left">
            <span className="text-sm font-medium text-primary300">{info.row.original.code}</span>
            <span className="text-xs text-gray-500">{info.row.original.address}</span>
          </div>
        );
      },
    }),

    columnHelper.accessor("consortium_name" as any, {
      header: () => <div className="w-full text-center">Consórcio</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }
        return (
          <div className="w-full text-center text-primary300">
            {info.row.original.consortium_name}
          </div>
        );
      },
    }),
    columnHelper.accessor("power_plant_name" as any, {
      header: () => <div className="w-full text-center">Usina</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }
        return (
          <div className="w-full text-center text-primary300">
            {info.row.original.power_plant_name}
          </div>
        );
      },
    }),
    columnHelper.accessor("allocation_value" as any, {
      header: () => <div className="w-full text-center">Carta de Rateio</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }
        const value = info.row.original.allocation_value;
        const type = info.row.original.allocation_type;

        if (type === "percentage") {
          return <div className="w-full text-center text-primary300">{value}%</div>;
        } else if (type === "order") {
          return <div className="w-full text-center text-primary300">{value}º</div>;
        }
        return <div className="w-full text-center text-gray-400">-</div>;
      },
    }),
    columnHelper.accessor("total_injected" as any, {
      header: () => <div className="w-full text-center">Total Produzido Usina</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // Consumer: SOMA dos totais injetados das facilities
          const total = info.row.original.subRows.reduce(
            (sum: number, row: any) => sum + (row.total_injected || 0),
            0,
          );
          return (
            <div className="w-full text-center font-semibold text-primary500">
              {total > 0 ? `${formatNumberWithCommas(total)} kWh` : "-"}
            </div>
          );
        }
        const value = info.row.original.total_injected;
        return (
          <div className="w-full text-center text-primary300">
            {value > 0 ? `${formatNumberWithCommas(value)} kWh` : "0 kWh"}
          </div>
        );
      },
    }),
    columnHelper.accessor("injected_in_facility" as any, {
      header: () => <div className="w-full text-center">Injeção Rateada</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // Consumer: SOMA dos injetados nas facilities
          const total = info.row.original.subRows.reduce(
            (sum: number, row: any) => sum + (row.injected_in_facility || 0),
            0,
          );
          return (
            <div className="w-full text-center font-semibold text-primary500">
              {total > 0 ? `${formatNumberWithCommas(total)} kWh` : "-"}
            </div>
          );
        }
        const value = info.row.original.injected_in_facility;
        return (
          <div className="w-full text-center text-primary300">
            {value > 0 ? `${formatNumberWithCommas(value)} kWh` : "0 kWh"}
          </div>
        );
      },
    }),
    columnHelper.accessor("compensated_kwh" as any, {
      header: () => <div className="w-full text-center">Energia Injetada</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // Consumer: SOMA dos compensated_kwh das facilities
          const total = info.row.original.subRows.reduce(
            (sum: number, row: any) => sum + (row.compensated_kwh || 0),
            0,
          );
          return (
            <div className="w-full text-center font-semibold text-primary500">
              {total > 0 ? `${formatNumberWithCommas(total)} kWh` : "-"}
            </div>
          );
        }
        const value = info.row.original.compensated_kwh;
        return (
          <div className="w-full text-center text-primary300">
            {value > 0 ? `${formatNumberWithCommas(value)} kWh` : "0 kWh"}
          </div>
        );
      },
    }),
    columnHelper.accessor("monthly_kwh" as any, {
      header: () => <div className="w-full text-center">Consumo</div>,
      cell: (info: any) => {
        if (info.row.depth > 0) {
          return <div className="w-full text-center text-gray-400">-</div>;
        }

        const value = info.row.original.monthly_kwh;
        return (
          <div className="w-full text-center font-semibold text-primary500">
            {value > 0 ? `${formatNumberWithCommas(value)} kWh` : "-"}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "expander",
      header: () => null,
      cell: (info: any) => {
        if (info.row.depth === 0 && info.row.original.subRows?.length > 0) {
          return (
            <div className="flex items-center justify-center">
              <FiChevronDown className="text-gray-400" size={16} />
            </div>
          );
        }
        return <div />;
      },
    }),
  ];

  return (
    <Layout>
      <PageContainer>
        <Typography variant="h3">Energia Injetada</Typography>

        <ListSection className="flex-col md:flex-row">
          <Typography variant="h4">Listagem</Typography>

          <FiltersContainer className="flex items-center gap-4 pt-5 md:pt-0">
            <WarningTooltip message="Atenção: Esta página mostra a separação de energia injetada para cada usina. Os dados são calculados a partir das faturas dos consumidores associados." />
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => {
                updateFilter("year", value);
              }}
              defaultValue={filters.year}
            />
            <Dropdown
              options={completMonth}
              onSelect={(value) => {
                updateFilter("month", value);
              }}
              defaultValue={filters.month}
            />
          </FiltersContainer>
        </ListSection>

        <Table data={tableData} columns={columns} onClickRow={null} />

        <div className="pt-18">
          <FaqCardComponent variant="questionSection" />
        </div>
      </PageContainer>
    </Layout>
  );
};

export default InjectedEnergyPage;
